/** Shared NPC dialog / case-notes layout — mobile full overlay vs desktop card. */

import {
  computeViewportInsets,
  readLiveViewportInsets,
  type ViewportInsets,
} from "../lib/viewport-insets.js";
import { isVisualViewportBoundsSynced } from "../lib/visual-viewport-bounds.js";
import { isMobileNpcOverlay } from "./viewport-breakpoint.js";

export { MOBILE_NPC_OVERLAY_BREAKPOINT, isMobileNpcOverlay } from "./viewport-breakpoint.js";

export const DIALOG_FONT = {
  title: 18,
  body: 16,
  button: 16,
  choice: 15,
} as const;

export const PANEL_MAX_W = 560;
export const PANEL_DESKTOP_MIN_H = 440;
export const PANEL_DESKTOP_MAX_H = 640;

export type DialogShellMetrics = {
  mobile: boolean;
  vw: number;
  vh: number;
  dimColor: number;
  dimAlpha: number;
  panelW: number;
  panelH: number;
  panelCx: number;
  panelCy: number;
  panelTop: number;
  panelBottom: number;
  innerW: number;
  pad: number;
  topInsetPx: number;
  bottomInsetPx: number;
  titleFont: number;
  bodyFont: number;
  buttonFont: number;
  lineGap: number;
};

const MOBILE_SHELL_OUTER_PAD = 12;

/** Panel chrome for case-notes or interview (footerReservePx used for min mobile height). */
export function computeDialogShell(
  vw: number,
  vh: number,
  _footerReservePx: number,
  insetsOverride?: ViewportInsets
): DialogShellMetrics {
  const mobile = isMobileNpcOverlay(vw);
  const pad = mobile ? 16 : 20;
  const dimColor = mobile ? 0x000000 : 0x070a10;
  const dimAlpha = mobile ? 0.92 : 0.55;

  const boundsSynced =
    typeof window !== "undefined" ? isVisualViewportBoundsSynced() : false;
  const liveInsets =
    insetsOverride ??
    (typeof window !== "undefined"
      ? readLiveViewportInsets(vw, window, { boundsSynced })
      : computeViewportInsets({ vw, boundsSynced }));

  let panelW: number;
  let panelH: number;
  let panelCx: number;
  let panelCy: number;
  let topInsetPx: number;
  let bottomInsetPx: number;

  if (mobile) {
    topInsetPx = liveInsets.top;
    bottomInsetPx = liveInsets.bottom;
    panelW = Math.max(280, vw - 24);
    const panelTop = topInsetPx + MOBILE_SHELL_OUTER_PAD;
    const panelBottom = vh - bottomInsetPx - MOBILE_SHELL_OUTER_PAD;
    panelH = Math.max(320, panelBottom - panelTop);
    panelCy = panelTop + panelH * 0.5;
    panelCx = vw * 0.5;
  } else {
    topInsetPx = 0;
    bottomInsetPx = 0;
    panelW = Math.min(PANEL_MAX_W, Math.max(320, vw - 48));
    panelH = Math.min(PANEL_DESKTOP_MAX_H, Math.max(PANEL_DESKTOP_MIN_H, vh * 0.72));
    panelCx = vw * 0.5;
    panelCy = vh * 0.5;
  }

  const panelTop = panelCy - panelH * 0.5;
  const panelBottom = panelCy + panelH * 0.5;
  const innerW = panelW - pad * 2;

  return {
    mobile,
    vw,
    vh,
    dimColor,
    dimAlpha,
    panelW,
    panelH,
    panelCx,
    panelCy,
    panelTop,
    panelBottom,
    innerW,
    pad,
    topInsetPx,
    bottomInsetPx,
    titleFont: DIALOG_FONT.title,
    bodyFont: DIALOG_FONT.body,
    buttonFont: DIALOG_FONT.button,
    lineGap: 10,
  };
}

/** Transcript / personality body height given fixed header and footer bands. */
export function bodyViewportHeight(
  panelH: number,
  headerPx: number,
  footerPx: number,
  gapPx = 12
): number {
  return Math.max(80, panelH - headerPx - footerPx - gapPx);
}
